import { generateURL, parseURL } from '@archipro-website/top-navigation';

/**
 * Check if a URL path is the products page. This includes the product
 * suppliers page.
 * @param urlPath The URL path to check.
 * @returns The URL path is the products page.
 */
function isProductsPage(urlPath: string) {
    return urlPath.startsWith('/products');
}

/**
 * Check if a URL path is the product suppliers page.
 * @param urlPath The URL path to check.
 * @returns The URL path is the product suppliers page.
 */
export function isProductSuppliersPage(urlPath: string) {
    return isProductsPage(urlPath) && urlPath.includes('/suppliers');
}

/**
 * Convert a products page URL path to a product suppliers page URL path. Does
 * nothing if the URL path is not the products page or is already the product
 * suppliers page.
 * @param urlPath The URL path to convert.
 * @returns The product suppliers page URL path.
 */
export function convertToProductSuppliersUrl(urlPath: string): string {
    if (!isProductsPage(urlPath) || isProductSuppliersPage(urlPath)) {
        return urlPath;
    }
    return urlPath.replace('/products', '/products/suppliers');
}

/**
 * Convert a product suppliers page URL path to a products page URL path. Does
 * nothing if the URL path is not the product suppliers page.
 * @param urlPath The URL path to convert.
 * @returns The products page URL path.
 */
export function convertFromProductSuppliersUrl(urlPath: string): string {
    if (!isProductSuppliersPage(urlPath)) {
        return urlPath;
    }
    return urlPath.replace('/suppliers', '');
}

/**
 * Build the toggle URL for switching between product and supplier category
 * pages while preserving only the category path and sort order.
 * @param url The URL to build the toggle URL for (current URL).
 * @returns The toggle URL.
 */
export function getProductSuppliersToggleUrl(url: string): string {
    const isSuppliersPage = isProductSuppliersPage(url);
    const baseProductUrl = isSuppliersPage
        ? convertFromProductSuppliersUrl(url)
        : url;
    const { basePath, params } = parseURL(baseProductUrl);
    const nextBasePath = isSuppliersPage
        ? basePath
        : convertToProductSuppliersUrl(basePath);

    return generateURL(
        nextBasePath,
        params.sort ? { sort: params.sort } : undefined,
        true,
    );
}
